/**
 * OperationListenDebrief — the seven-screen post-game analysis report, per
 * "Operation Listen — Post-Game Analysis Design" §12.
 *
 * The screens map 1:1 to the design doc:
 *   1. Round rating
 *   2. The heat curve
 *   3. Who carried the round
 *   4. Your voice           ← placeholder (voice capture is build-step 3-4)
 *   5. What the Conflict said back  (humans check-in; capture here too)
 *   6. The room
 *   7. Next practice
 *
 * Props:
 *   debrief: the olg_debriefs doc returned by the backend
 *   onSubmitHumanAnswer({role, q1, q2, q3}) → Promise<debrief>
 *   onClose(): close the overlay
 */
import React, { useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Flame, BarChart3, Mic, MessageSquare, Users, Target, AlertTriangle } from 'lucide-react';
import { OL } from './OperationListenParts';

const RUNG_SHORT = ['Feel', 'One-off?', 'Old/New?', 'Purpose?', 'Fault?', 'Proof?', 'Ask/Demand?'];
const LABEL_COLOR = {
  'self-led':      '#3B7A3B',   // forest green — the Guide owned it
  'chip-assisted': '#8CA66D',   // soft moss
  'nudge-led':     '#D4863A',   // amber (same as nudge)
  'coach-fed':     '#C45A3B',   // clay
  'not-covered':   '#CFC8B8',   // dim
};
const LABEL_TEXT = {
  'self-led': 'Self-led',
  'chip-assisted': 'Chip-assisted',
  'nudge-led': 'Nudge-led',
  'coach-fed': 'Coach-fed',
  'not-covered': 'Not covered',
};

const Btn = ({ children, onClick, variant = 'primary', testId, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      padding: '8px 14px', borderRadius: 10,
      background: variant === 'primary' ? OL.clay : (variant === 'ghost' ? 'transparent' : OL.surface2),
      border: `1px solid ${variant === 'primary' ? OL.clay : OL.border2}`,
      color: variant === 'primary' ? '#fff' : OL.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      fontSize: 13, fontWeight: 600, letterSpacing: '0.02em',
      touchAction: 'manipulation',
    }}
  >{children}</button>
);

const ScorePill = ({ label, value, measured, suffix = '/4' }) => (
  <div style={{
    background: OL.surface2, border: `1px solid ${OL.border2}`,
    borderRadius: 10, padding: '8px 10px', minWidth: 90,
  }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: OL.textDim }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: OL.text, marginTop: 2 }}>
      {value}{suffix}
    </div>
    {measured !== undefined && measured !== value && (
      <div style={{ fontSize: 10, color: OL.textDim, marginTop: 2 }}>
        measured {measured}{suffix}
      </div>
    )}
  </div>
);

// ---------- Screen 1 : Round rating ----------
const ScreenRating = ({ scores }) => {
  const { final = {}, measured = {}, roundRating, label, tier,
          confidenceLabel, confidenceNotes = [], mode = {} } = scores || {};
  const summary = useMemo(() => {
    const parts = [];
    if (scores?.rungsCovered >= 7) parts.push('walked all seven rungs');
    else parts.push(`${scores?.rungsCovered || 0} of 7 rungs covered`);
    if (scores?.nudgesSent) parts.push(`${scores.nudgesSent} nudge${scores.nudgesSent === 1 ? '' : 's'} sent`);
    if (scores?.threatPeak >= 3) parts.push(`held heat at ${scores.threatPeak}`);
    if (scores?.unfinished) parts.push('round left unfinished');
    return parts.length ? `You ${parts.join(', ')}.` : 'A short round.';
  }, [scores]);
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 48, fontWeight: 800, color: OL.clay }}>{roundRating ?? '—'}</div>
        <div style={{ fontSize: 13, color: OL.textDim }}>/ 4</div>
        <div style={{
          marginLeft: 'auto',
          padding: '4px 10px', borderRadius: 999,
          background: label === 'Strong' ? OL.clayGlow : OL.surface2,
          border: `1px solid ${label === 'Strong' ? OL.clay : OL.border2}`,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: label === 'Strong' ? OL.clay : OL.text,
        }}>{label || 'Unrated'}</div>
      </div>
      <div style={{ fontSize: 12, color: OL.textDim, marginTop: 4 }}>
        {tier && <span>Tier: <b style={{ color: OL.text }}>{tier}</b> · </span>}
        <span>Mode: {mode.gameMode || 'beginner'} · {mode.roleMode || '4p'}</span>
        {confidenceLabel === 'lower' && (
          <span style={{ color: OL.amber }}> · lower confidence</span>
        )}
      </div>
      <p style={{ marginTop: 14, fontSize: 14, color: OL.text, lineHeight: 1.5 }}>{summary}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <ScorePill label="Control"      value={final.control ?? 0}      measured={measured.control} />
        <ScorePill label="Guidance"     value={final.guidance ?? 0}     measured={measured.guidance} />
        <ScorePill label="Landing"      value={final.landing ?? 0}      measured={measured.landing} />
        <ScorePill label="Independence" value={final.independence ?? 0} measured={measured.independence} />
      </div>
      {confidenceNotes.length > 0 && (
        <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 11, color: OL.textDim }}>
          {confidenceNotes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {scores?.gaps?.length > 0 && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8,
                      background: 'rgba(212,134,58,0.10)', border: `1px solid ${OL.amber}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: OL.amber, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            <AlertTriangle size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: '-2px' }} />
            Gap flag — measured vs their view
          </div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: OL.text }}>
            {scores.gaps.map((g, i) => (
              <li key={i}><b>{g.pillar}</b>: you measured {g.measured}, they said {g.human}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------- Screen 2 : Heat curve ----------
const ScreenHeat = ({ scores, events }) => {
  const series = scores?.threatSeries || [{ ts: null, value: 1 }];
  const max = 4;
  // Normalise series into x positions by index (no timestamps for first point).
  const points = series.map((p, i) => ({
    x: (i / Math.max(1, series.length - 1)) * 100,
    y: 100 - ((p.value - 0) / max) * 100,
    v: p.value,
  }));
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  // Overlay markers for key events along the same x axis.
  const markers = useMemo(() => {
    const out = [];
    const totalEvents = (events || []).length;
    (events || []).forEach((ev, i) => {
      const x = totalEvents <= 1 ? 50 : (i / (totalEvents - 1)) * 100;
      if (ev.type === 'nudge-on')      out.push({ x, kind: 'nudge', label: `nudge r${ev.rung + 1}` });
      else if (ev.type === 'rung-tap-on') out.push({ x, kind: 'rung', label: `rung ${ev.rung + 1}` });
      else if (ev.type === 'phase-complete') out.push({ x, kind: 'phase', label: ev.phase });
    });
    return out.slice(0, 40); // cap for sanity
  }, [events]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Flame size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>Heat curve</div>
      </div>
      <div style={{ fontSize: 11, color: OL.textDim, marginBottom: 8 }}>
        Threat meter over the round · peak <b style={{ color: OL.text }}>{scores?.threatPeak}</b> · ended at <b style={{ color: OL.text }}>{scores?.threatEnd}</b>
      </div>
      <div style={{ position: 'relative', background: OL.surface2, border: `1px solid ${OL.border2}`,
                    borderRadius: 10, height: 160, padding: 8 }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          {[1, 2, 3, 4].map((lvl) => (
            <line key={lvl} x1="0" x2="100" y1={100 - (lvl / max) * 100} y2={100 - (lvl / max) * 100}
                  stroke={OL.border2} strokeDasharray="1,2" strokeWidth="0.3" />
          ))}
          <path d={path} fill="none" stroke={OL.clay} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="1.2" fill={OL.clay} vectorEffect="non-scaling-stroke" />
          ))}
          {markers.map((m, i) => (
            <circle key={i} cx={m.x} cy="98"
                    r="1.1"
                    fill={m.kind === 'nudge' ? OL.amber : (m.kind === 'rung' ? '#3B7A3B' : OL.textDim)}
                    vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        {[1, 2, 3, 4].map((lvl) => (
          <div key={lvl} style={{
            position: 'absolute', right: 10, top: `calc(${100 - (lvl / max) * 100}% - 7px)`,
            fontSize: 9, color: OL.textDim,
          }}>{lvl}</div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: OL.textDim, marginTop: 8 }}>
        Dots along the bottom: <span style={{ color: '#3B7A3B' }}>rung taps</span> ·{' '}
        <span style={{ color: OL.amber }}>nudges</span> ·{' '}
        <span style={{ color: OL.textDim }}>phase marks</span>
      </div>
    </div>
  );
};

// ---------- Screen 3 : Who carried the round ----------
const ScreenRungs = ({ scores }) => {
  const labels = scores?.rungLabels || Array(7).fill('not-covered');
  const nudgeCap = (scores?.mode?.gameMode === 'advanced') ? 2 : 7;
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <BarChart3 size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>Who carried the round</div>
      </div>
      <div style={{ display: 'flex', gap: 3, marginBottom: 10 }}>
        {labels.map((lbl, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: 24, borderRadius: 4, marginBottom: 4,
              background: LABEL_COLOR[lbl] || OL.border2,
            }} title={LABEL_TEXT[lbl]} />
            <div style={{ fontSize: 9, color: OL.textDim }}>{i + 1}</div>
            <div style={{ fontSize: 9, color: OL.textDim, whiteSpace: 'nowrap' }}>{RUNG_SHORT[i]}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 10, color: OL.textDim }}>
        {['self-led', 'chip-assisted', 'nudge-led', 'coach-fed', 'not-covered'].map((lbl) => (
          <div key={lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: LABEL_COLOR[lbl], display: 'inline-block' }} />
            {LABEL_TEXT[lbl]}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: OL.text }}>
        Nudges: <b>{scores?.nudgesSent || 0}</b> of {nudgeCap} allowed
        {scores?.nudgesActiveAtEnd > 0 && (
          <span style={{ color: OL.amber }}> · {scores.nudgesActiveAtEnd} still active at round end</span>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: OL.textDim }}>
        Credit share <b style={{ color: OL.text }}>{Math.round((scores?.creditShare || 0) * 100)}%</b> · Independence score {scores?.measured?.independence}/4
      </div>
    </div>
  );
};

// ---------- Screen 4 : Your voice ----------
const ScreenVoice = ({ scores }) => {
  const voice = scores?.voice || {};
  const guide = voice.guide || null;
  const conflict = voice.playerB || voice.conflict || null;
  const hasAny = guide || conflict;
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Mic size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>Your voice</div>
      </div>
      {!hasAny ? (
        <div style={{ padding: 16, borderRadius: 10, background: OL.surface2,
                      border: `1px dashed ${OL.border2}`, color: OL.textDim, fontSize: 12, lineHeight: 1.55 }}>
          No voice capture this round — enable the mic chip on the Guide or
          Conflict panel to measure loudness, pitch, pace, and talk-time against
          each player's own baseline.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {guide && <VoiceCard title="Guide" data={guide} />}
          {conflict && <VoiceCard title="Conflict" data={conflict} />}
          {scores?.controlVoiceNote && (
            <div style={{ padding: 10, borderRadius: 8,
                          background: 'rgba(212,134,58,0.10)', border: `1px solid ${OL.amber}`,
                          fontSize: 11, color: OL.text }}>
              Control voice note: <b>{scores.controlVoiceNote}</b>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const VoiceCard = ({ title, data }) => (
  <div style={{ padding: 12, borderRadius: 10, background: OL.surface2, border: `1px solid ${OL.border2}` }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
      {title}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, color: OL.text }}>
      <div>Speaking: <b>{data.speakingSeconds || 0}s</b></div>
      <div>Talk share: <b>{Math.round((data.talkTimeShare || 0) * 100)}%</b></div>
      <div>Mean dB: <b>{data.meanLoudnessDb ?? '—'}</b></div>
      <div>Δ vs baseline:{' '}
        <b style={{
          color: (data.meanLoudnessDelta ?? 0) > 3 ? OL.danger
               : (data.meanLoudnessDelta ?? 0) < -1 ? '#3B7A3B' : OL.text,
        }}>{data.meanLoudnessDelta != null ? `${data.meanLoudnessDelta > 0 ? '+' : ''}${data.meanLoudnessDelta} dB` : '—'}</b>
      </div>
      <div>Overlap: <b>{data.overlapSeconds || 0}s</b></div>
      <div>Baseline: <b>{data.baseline?.loudness_db ?? '—'} dB</b></div>
    </div>
  </div>
);

// ---------- Screen 5 : What the Conflict said back (check-in capture) ----------
const CheckInRow = ({ q, value, onChange, testIdBase }) => (
  <div style={{ padding: '10px 0', borderBottom: `1px solid ${OL.border}` }}>
    <div style={{ fontSize: 12, color: OL.text, marginBottom: 6 }}>{q}</div>
    <div style={{ display: 'inline-flex', gap: 6 }}>
      {['yes', 'partly', 'no'].map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          data-testid={`${testIdBase}-${v}`}
          data-active={value === v ? 'true' : 'false'}
          style={{
            padding: '6px 12px', borderRadius: 999,
            background: value === v ? OL.clay : 'transparent',
            border: `1px solid ${value === v ? OL.clay : OL.border2}`,
            color: value === v ? '#fff' : OL.text,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', cursor: 'pointer', touchAction: 'manipulation',
          }}
        >{v}</button>
      ))}
    </div>
  </div>
);

const ScreenCheckIn = ({ debrief, onSubmitHumanAnswer }) => {
  const [conflict, setConflict] = useState(debrief?.humanAnswers?.conflict || {});
  const [cc, setCc] = useState(debrief?.humanAnswers?.conflictCoach || {});
  const [saving, setSaving] = useState(null);
  const roleMode = debrief?.state?.roleMode || '4p';
  const hasCc = roleMode !== '2p';

  const submit = async (role, patch) => {
    setSaving(role);
    try {
      await onSubmitHumanAnswer({ role, ...patch });
    } finally {
      setSaving(null);
    }
  };

  const bind = (setter, role) => (field, val) => {
    const next = { ...(role === 'conflict' ? conflict : cc), [field]: val };
    if (role === 'conflict') setConflict(next); else setCc(next);
    submit(role, { q1: next.q1, q2: next.q2, q3: next.q3 });
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <MessageSquare size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>What the Conflict said back</div>
      </div>
      <div style={{ padding: 10, borderRadius: 10, background: OL.surface2, border: `1px solid ${OL.border2}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
          Conflict
        </div>
        <CheckInRow q="1 · Did you feel heard?"                    value={conflict.q1}
                    onChange={(v) => bind(setConflict, 'conflict')('q1', v)}
                    testIdBase="debrief-cf-q1" />
        <CheckInRow q="2 · Did the Guide stay steady when it got heated?" value={conflict.q2}
                    onChange={(v) => bind(setConflict, 'conflict')('q2', v)}
                    testIdBase="debrief-cf-q2" />
        <CheckInRow q="3 · Would you follow through on where it ended?"   value={conflict.q3}
                    onChange={(v) => bind(setConflict, 'conflict')('q3', v)}
                    testIdBase="debrief-cf-q3" />
      </div>
      {hasCc && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: OL.surface2, border: `1px solid ${OL.border2}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
            Conflict Coach
          </div>
          <CheckInRow q="1 · Did the heat come down because of what the Guide did?" value={cc.q1}
                      onChange={(v) => bind(setCc, 'conflictCoach')('q1', v)}
                      testIdBase="debrief-cc-q1" />
          <CheckInRow q="2 · Did the Guide's voice stay calm and steady when you raised the threat?" value={cc.q2}
                      onChange={(v) => bind(setCc, 'conflictCoach')('q2', v)}
                      testIdBase="debrief-cc-q2" />
          <CheckInRow q="3 · Did it end with something real the Conflict could act on?" value={cc.q3}
                      onChange={(v) => bind(setCc, 'conflictCoach')('q3', v)}
                      testIdBase="debrief-cc-q3" />
        </div>
      )}
      {saving && (
        <div style={{ fontSize: 10, color: OL.textDim, marginTop: 8 }}>Saving {saving}…</div>
      )}
      {debrief?.transcript?.length > 0 && (
        <details style={{ marginTop: 14 }} data-testid="debrief-transcript">
          <summary style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
            Transcript · {debrief.transcript.length} lines
          </summary>
          <div style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto',
                        background: OL.surface2, border: `1px solid ${OL.border2}`, borderRadius: 8, padding: 10 }}>
            {debrief.transcript.map((ln, i) => (
              <div key={i} style={{ fontSize: 11, lineHeight: 1.5, marginBottom: 4, color: OL.text }}>
                <b style={{ color: OL.textDim }}>{ln.actor}:</b> {ln.text}
                {typeof ln.confidence === 'number' && ln.confidence < 0.5 && (
                  <span style={{ color: OL.amber, marginLeft: 4 }}>· low conf</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
      {debrief?.scores?.matched?.commitmentEvidence && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                      background: OL.surface2, border: `1px solid ${OL.border2}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Closing commitment · level {debrief.scores.matched.commitmentLevel}/4
          </div>
          <div style={{ fontSize: 12, color: OL.text, marginTop: 4, fontStyle: 'italic' }}>
            "{debrief.scores.matched.commitmentEvidence}"
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Screen 6 : The room ----------
const ScreenRoom = ({ debrief }) => {
  const events = debrief?.events || [];
  const nudgesSent = events.filter((e) => e.type === 'nudge-on').length;
  const nudgesDismissed = events.filter((e) => e.type === 'nudge-off').length;
  const phaseMarks = events.filter((e) => e.type === 'phase-complete').length;
  const threatMoves = events.filter((e) => e.type === 'threat-change').length;
  const dignityPick = events.find((e) => e.type === 'dignity-pick');
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Users size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>The room</div>
      </div>
      <div style={{ padding: 12, borderRadius: 10, background: OL.surface2, border: `1px solid ${OL.border2}`, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Coach panel</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: OL.text, lineHeight: 1.6 }}>
          <li>Nudges sent: <b>{nudgesSent}</b> · dismissed: <b>{nudgesDismissed}</b></li>
          <li>Phase marks: <b>{phaseMarks}</b></li>
          <li>Dignity statement: {dignityPick ? <><b>{dignityPick.category}</b> — "{dignityPick.phrase}"</> : <i>none picked</i>}</li>
        </ul>
      </div>
      <div style={{ padding: 12, borderRadius: 10, background: OL.surface2, border: `1px solid ${OL.border2}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: OL.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Conflict Coach panel</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: OL.text, lineHeight: 1.6 }}>
          <li>Meter moves: <b>{threatMoves}</b></li>
          <li>Peak threat reached: <b>{debrief?.scores?.threatPeak}</b></li>
          <li>Reaction time: <i style={{ color: OL.textDim }}>needs voice capture to measure</i></li>
        </ul>
      </div>
    </div>
  );
};

// ---------- Screen 7 : Next practice ----------
const ScreenNextPractice = ({ scores }) => {
  const picks = scores?.nextPractice || [];
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Target size={16} color={OL.clay} />
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>Next practice</div>
      </div>
      {picks.length === 0 ? (
        <div style={{ fontSize: 12, color: OL.textDim }}>No suggestions — you nailed this one.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {picks.map((pick, i) => (
            <div key={i} style={{ padding: 12, borderRadius: 10,
                                  background: OL.surface2, border: `1px solid ${OL.border2}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: OL.text }}>{pick.title}</div>
              <div style={{ fontSize: 12, color: OL.textDim, marginTop: 4 }}>{pick.body}</div>
              <div style={{ fontSize: 10, color: OL.clay, marginTop: 6, letterSpacing: '0.05em' }}>
                {pick.drills.join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Screen · Stage Readout (Guide-only heatmap) ----------
// Mirrors the "Post Game Build" doc: 9 stage cards, color-coded by tier,
// each with 2 lines of narrative + one actionable suggestion.
const TIER_META = {
  'great':      { bg: 'rgba(59,122,59,0.14)',  border: '#3B7A3B', chip: '#3B7A3B', label: 'Great'       },
  'good':       { bg: 'rgba(140,166,109,0.14)',border: '#8CA66D', chip: '#8CA66D', label: 'Good'        },
  'needs-work': { bg: 'rgba(212,134,58,0.14)', border: '#D4863A', chip: '#D4863A', label: 'Needs work'  },
  'missed':     { bg: 'rgba(155,37,37,0.12)',  border: '#9B2525', chip: '#C45A3B', label: 'Missed'      },
};

const StageCard = ({ stage }) => {
  const meta = TIER_META[stage.tier] || TIER_META['needs-work'];
  const summary = Array.isArray(stage.summary) ? stage.summary : [stage.summary];
  return (
    <div
      data-testid={`stage-card-${stage.key}`}
      style={{
        background: meta.bg, border: `1px solid ${meta.border}`,
        borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, flex: 1 }}>{stage.title}</div>
        <div style={{
          padding: '2px 8px', borderRadius: 999, background: meta.chip, color: '#fff',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>{meta.label}</div>
        <div style={{ fontSize: 11, color: OL.textDim, minWidth: 32, textAlign: 'right' }}>{stage.score}</div>
      </div>
      {/* Empathy Ladder sub-cells */}
      {Array.isArray(stage.cells) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 2 }}>
          {stage.cells.map((c) => {
            const cm = TIER_META[c.tier] || TIER_META['needs-work'];
            return (
              <div key={c.index}
                data-testid={`stage-rung-${c.index}`}
                title={`${c.title}${c.coachFed ? ' · coach-fed' : ''}`}
                style={{
                  background: cm.chip, color: '#fff', borderRadius: 5, padding: '4px 0',
                  textAlign: 'center', fontSize: 9, fontWeight: 700,
                }}
              >{c.index + 1}</div>
            );
          })}
        </div>
      )}
      {summary.map((line, i) => (
        <div key={i} style={{ fontSize: 12, color: OL.text, lineHeight: 1.45 }}>{line}</div>
      ))}
      {stage.suggestion && (
        <div style={{
          marginTop: 2, padding: '6px 8px', borderRadius: 6,
          background: 'rgba(0,0,0,0.18)', border: `1px dashed ${OL.border2}`,
          fontSize: 11, color: OL.textDim, lineHeight: 1.45,
        }}>
          <b style={{ color: OL.cream, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Try next:</b> {stage.suggestion}
        </div>
      )}
    </div>
  );
};

const ScreenStageReadout = ({ debrief }) => {
  const stages = debrief?.stageReadout || [];
  if (!stages.length) {
    return (
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: 13, color: OL.textDim }}>
          No stage readout for this round — recording wasn't enabled or the round ended too fast.
        </p>
      </div>
    );
  }
  const overall = Math.round(stages.reduce((a, s) => a + (s.score || 0), 0) / stages.length);
  const overallTier = overall >= 85 ? 'great' : overall >= 65 ? 'good' : overall >= 35 ? 'needs-work' : 'missed';
  const overallMeta = TIER_META[overallTier];
  return (
    <div style={{ padding: 16 }} data-testid="debrief-stage-readout">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: OL.textDim }}>Guide readout</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            padding: '3px 8px', borderRadius: 999, background: overallMeta.chip,
            color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>{overallMeta.label}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: OL.text }}>{overall}<span style={{ color: OL.textDim, fontSize: 11 }}>/100</span></span>
        </div>
      </div>
      <p style={{ fontSize: 12, color: OL.textDim, marginBottom: 12, lineHeight: 1.5 }}>
        Nine stages from the gameplay flow, scored from the transcript + your toggles. Red = missed, amber = partial, green = landed.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {stages.map((s) => <StageCard key={s.key} stage={s} />)}
      </div>
    </div>
  );
};

const SCREENS = [
  { key: 'rating',       title: 'Round rating',             Comp: ScreenRating },
  { key: 'stages',       title: 'Stage readout',            Comp: ScreenStageReadout, guideOnly: true },
  { key: 'heat',         title: 'The heat curve',           Comp: ScreenHeat },
  { key: 'rungs',        title: 'Who carried the round',    Comp: ScreenRungs },
  { key: 'voice',        title: 'Your voice',               Comp: ScreenVoice },
  { key: 'checkin',      title: 'What the Conflict said back', Comp: ScreenCheckIn },
  { key: 'room',         title: 'The room',                 Comp: ScreenRoom },
  { key: 'next',         title: 'Next practice',            Comp: ScreenNextPractice },
];

// When voice recording was never enabled during the round the debrief
// collapses to the two human questionnaires — Conflict + Conflict Coach —
// per the facilitator spec. Everything else depends on captured evidence,
// so we hide it rather than show empty cards.
const SCREENS_NO_VOICE = [
  { key: 'checkin', title: 'What the Conflict said back', Comp: ScreenCheckIn },
];

// ---------- Next Step 7-check card (post-paraphrase steering) ----------
const NextStepCard = ({ debrief }) => {
  const ns = debrief?.nextStepAnalysis;
  if (!ns) return null;
  const meta = TIER_META[ns.tier] || TIER_META['needs-work'];
  const checks = [
    { label: 'Loop-back spotted',       value: `${ns.loopBacks?.length || 0}`,
      ok: (ns.loopBacks?.length || 0) > 0, neutral: true,
      hint: "Each place the chat returned to something from the empathy ladder." },
    { label: 'Steered toward consequence', value: `${ns.steeredCount}`,
      ok: ns.steeredCount > 0,
      hint: "Did the Guide or Coach move the talk to 'what happens if' instead of staying in the feeling?" },
    { label: 'Both directions covered', value: ns.bothDirections ? 'Yes' : 'No',
      ok: ns.bothDirections,
      hint: "Explored both 'if you do' and 'if you don't' for the action / decision / commitment." },
    { label: 'Confirmed answers reached', value: `${ns.confirmedAnswers}`,
      ok: ns.confirmedAnswers > 0,
      hint: "A clear yes or no landed for each action, decision, or commitment." },
    { label: 'Time frame attached',     value: ns.timeFrameAttached ? 'Yes' : 'No',
      ok: ns.timeFrameAttached,
      hint: "Each confirmed commitment names when (now, tomorrow, next week)." },
    { label: 'Stuck in the feeling',    value: `${ns.stuckInFeeling?.length || 0}`,
      ok: (ns.stuckInFeeling?.length || 0) === 0, invert: true,
      hint: "Loop-backs that stayed in the emotion and never moved toward a decision." },
    { label: 'Who steered',             value: `Guide ${ns.steerBy?.guide || 0} · Coach ${ns.steerBy?.coach || 0}`,
      ok: (ns.steerBy?.guide || 0) + (ns.steerBy?.coach || 0) > 0,
      hint: "Who did the steering turns — Guide carrying it is the goal; Coach is a lift." },
  ];
  return (
    <div data-testid="debrief-next-step" style={{
      background: meta.bg, border: `1px solid ${meta.border}`,
      borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, flex: 1 }}>Next Step · steering</div>
        <div style={{
          padding: '2px 8px', borderRadius: 999, background: meta.chip, color: '#fff',
          fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>{meta.label}</div>
        <div style={{ fontSize: 11, color: OL.textDim, minWidth: 32, textAlign: 'right' }}>{ns.score}</div>
      </div>
      <div style={{ fontSize: 12, color: OL.textDim, lineHeight: 1.45 }}>
        After the paraphrase, every loop-back to the empathy ladder should get steered toward
        consequences and risk — "if you do" vs "if you don't", until each action, decision, and
        commitment lands a clear yes or no.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
        {checks.map((c, i) => (
          <div key={i} style={{
            padding: '6px 8px', borderRadius: 6,
            background: 'rgba(0,0,0,0.18)',
            border: `1px solid ${c.neutral ? OL.border2 : (c.ok ? OL.border2 : OL.danger)}`,
            display: 'flex', alignItems: 'center', gap: 6,
          }} title={c.hint}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: c.neutral ? '#8CA66D' : (c.ok ? '#3B7A3B' : '#C45A3B'),
            }} />
            <span style={{ fontSize: 11, color: OL.textDim, flex: 1 }}>{c.label}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: OL.text }}>{c.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Coach contribution + talk share strips ----------
const ContributionStrip = ({ debrief }) => {
  const cc = debrief?.coachContribution;
  const ts = debrief?.talkShare;
  if (!cc && !ts) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {cc && (
        <div data-testid="debrief-coach-contribution" style={{
          background: OL.surface2, border: `1px solid ${OL.border}`,
          borderRadius: 10, padding: 10,
        }}>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: OL.textDim, fontWeight: 700 }}>
            Coach contribution
          </div>
          <div style={{ fontSize: 12, color: OL.text, marginTop: 6, lineHeight: 1.5 }}>
            Fed <b>{cc.coachFedShare}%</b> of the ladder rungs.<br/>
            Steered <b>{cc.coachSteerTurns}</b> turn{cc.coachSteerTurns === 1 ? '' : 's'} ({cc.coachSteerShare}%).<br/>
            Dignity line {cc.dignitySuggested ? 'suggested' : 'not pushed'}.
          </div>
        </div>
      )}
      {ts && (
        <div data-testid="debrief-talk-share" style={{
          background: OL.surface2, border: `1px solid ${OL.border}`,
          borderRadius: 10, padding: 10,
        }}>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: OL.textDim, fontWeight: 700 }}>
            Carrying line
          </div>
          <div style={{ fontSize: 12, color: OL.text, marginTop: 6, lineHeight: 1.5 }}>
            <b>{ts.carrierLabel || '—'}</b> carried <b>{ts.carrierShare || 0}%</b> of the talk.<br/>
            Guide {ts.shares?.guide || 0}% · Conflict {ts.shares?.playerB || 0}%
            {(ts.shares?.coach || 0) > 0 && <> · Coach {ts.shares.coach}%</>}
            {(ts.shares?.conflict || 0) > 0 && <> · CC {ts.shares.conflict}%</>}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Conflict Coach perspective block ----------
const ConflictCoachPerspective = ({ debrief, onSubmitHumanAnswer }) => {
  const roleMode = debrief?.roleMode || '4p';
  // In 2P/3P games there's no CC seat, so this block is irrelevant.
  if (roleMode === '2p' || roleMode === '3p') return null;
  // Reuse the existing checkin screen's CC lane — pass only the CC half.
  return (
    <div data-testid="debrief-cc-perspective" style={{
      background: OL.surface2, border: `1px solid ${OL.border}`,
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: OL.textDim, fontWeight: 700 }}>
        Conflict Coach perspective
      </div>
      <ScreenCheckIn scenario="cc-only" debrief={debrief} onSubmitHumanAnswer={onSubmitHumanAnswer} />
    </div>
  );
};

export const OperationListenDebrief = ({ debrief, onSubmitHumanAnswer, onClose, onStartNewRound, viewerRole = 'facilitator' }) => {
  if (!debrief) return null;
  const canSeeGuideOnly = viewerRole === 'guide' || viewerRole === 'facilitator';
  const hasVoice = !!debrief?.scores?.hasVoice;
  const roleMode = debrief?.roleMode || '4p';

  return (
    <div
      data-testid="olg-debrief-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(10,10,8,0.65)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div
        data-testid="olg-debrief-panel"
        style={{
          width: '100%', maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          background: OL.surface, border: `1px solid ${OL.border}`,
          borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', borderBottom: `1px solid ${OL.border}`,
          background: OL.surface2,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: OL.textDim, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Post-game analysis
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: OL.text, flex: 1, textAlign: 'center' }}>
            {hasVoice ? 'Debrief · one page' : 'Debrief · check-in only'}
          </div>
          <button
            onClick={onClose}
            data-testid="olg-debrief-close"
            style={{
              padding: 6, borderRadius: 999, background: 'transparent',
              border: `1px solid ${OL.border2}`, color: OL.text, cursor: 'pointer',
            }}
          ><X size={14} /></button>
        </div>

        {/* Single scrollable page */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {hasVoice && canSeeGuideOnly && (
            <>
              {/* Section 1 — stage heatmap (start of readout) */}
              <ScreenStageReadout debrief={debrief} />

              {/* Section 2 — Next Step 7 checks */}
              <NextStepCard debrief={debrief} />

              {/* Section 3 — coach contribution + carrying line */}
              <ContributionStrip debrief={debrief} />

              {/* Section 4 — rungs who carried the round */}
              <ScreenRungs scores={debrief.scores} debrief={debrief} />

              {/* Section 6 — voice fingerprint */}
              <ScreenVoice debrief={debrief} />
            </>
          )}

          {/* Section 8 — Conflict + Conflict Coach questionnaires (always visible) */}
          <div data-testid="debrief-questionnaires">
            <ScreenCheckIn debrief={debrief} onSubmitHumanAnswer={onSubmitHumanAnswer} />
          </div>

          {hasVoice && canSeeGuideOnly && (
            <>
              <ScreenRoom debrief={debrief} />
              <ScreenNextPractice debrief={debrief} />
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderTop: `1px solid ${OL.border}`,
          background: OL.surface2,
        }}>
          <div style={{ flex: 1, fontSize: 11, color: OL.textDim }}>
            {hasVoice ? 'Full readout + check-ins' : 'Enable recording next round for the full stage readout.'}
          </div>
          {onStartNewRound ? (
            <Btn onClick={onStartNewRound} testId="olg-debrief-start-new">Start new round</Btn>
          ) : (
            <Btn onClick={onClose} testId="olg-debrief-done">Done</Btn>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationListenDebrief;
